/**
 * ChunkErrorBoundary — reload-once on a stale-deploy chunk failure.
 *
 * Every heavy route is `lazy()` (App.jsx). After a deploy every hashed chunk
 * name changes, so a tab still running the previous index.html that navigates
 * to a not-yet-loaded route requests a deleted `/assets/*.js` and the dynamic
 * import rejects (Vite: "Failed to fetch dynamically imported module",
 * "Loading chunk … failed", "error loading dynamically imported module";
 * Safari: "Importing a module script failed"). index.html is served
 * `no-store`, so a plain reload always recovers.
 *
 * Two hooks cover both places that failure surfaces:
 *   - `installChunkReloadListener()` — Vite's `vite:preloadError` window
 *     event (fired for chunks preloaded by a dynamic import's static
 *     dependency graph). `preventDefault()` stops Vite rethrowing.
 *   - `<ChunkErrorBoundary>` — the lazy import rejection itself, which React
 *     throws at the nearest error boundary.
 *
 * Either path reloads the page ONCE, guarded by a sessionStorage timestamp
 * so a genuinely broken chunk cannot loop: with the guard still fresh the
 * boundary renders a crash screen with a manual Reload button instead. The
 * guard expires after RELOAD_GUARD_TTL_MS so a later deploy in the same tab
 * session gets its own one free auto-reload. Non-chunk errors are rethrown to
 * the outer Sentry boundary (main.jsx) so their reporting is unchanged.
 */
import { Component } from "react";
import { ErrorMessage } from "./ErrorMessage";
import { Loading } from "./Loading";

export const CHUNK_RELOAD_KEY = "xring.chunkReloadAt";
export const RELOAD_GUARD_TTL_MS = 60 * 1000;

const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|Loading (?:CSS )?chunk .* failed|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

export function isChunkLoadError(err) {
  const msg = err == null ? "" : typeof err === "string" ? err : String(err.message ?? "");
  return CHUNK_ERROR_RE.test(msg);
}

function readGuard() {
  try {
    const raw = window.sessionStorage.getItem(CHUNK_RELOAD_KEY);
    const at = raw == null ? NaN : Number(raw);
    return Number.isFinite(at) && Date.now() - at < RELOAD_GUARD_TTL_MS;
  } catch {
    // No sessionStorage (private mode / blocked): treat the guard as SET so we
    // can never loop — the crash screen's manual Reload still works.
    return true;
  }
}

function writeGuard() {
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function clearChunkReloadGuard() {
  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    /* ignore */
  }
}

function defaultReload() {
  window.location.reload();
}

/** Reload once per guard window. Returns true when a reload was triggered. */
export function reloadOnceForChunkError(reload = defaultReload) {
  if (readGuard()) return false;
  writeGuard();
  reload();
  return true;
}

/** Listen for Vite's `vite:preloadError`; returns an uninstall function. */
export function installChunkReloadListener({ target, reload } = {}) {
  const t = target ?? (typeof window !== "undefined" ? window : null);
  if (!t) return () => {};
  const handler = (event) => {
    if (reloadOnceForChunkError(reload ?? defaultReload)) event.preventDefault?.();
  };
  t.addEventListener("vite:preloadError", handler);
  return () => t.removeEventListener("vite:preloadError", handler);
}

export function ChunkCrashScreen({ onReload }) {
  return (
    <div className="chunk-crash">
      <ErrorMessage
        message="This page couldn't load — a new version of X·RING may have been published."
        actions={[{ label: "Reload", onClick: onReload }]}
      />
    </div>
  );
}

export class ChunkErrorBoundary extends Component {
  state = { error: null, reloading: false };

  static getDerivedStateFromError(error) {
    // Decide the render up front (a read of the guard, no side effect) so the
    // first-failure path never flashes the crash screen before the reload.
    return { error, reloading: isChunkLoadError(error) && !readGuard() };
  }

  componentDidCatch(error) {
    if (!isChunkLoadError(error)) return;
    const reload = this.props.reload ?? defaultReload;
    if (!reloadOnceForChunkError(reload)) this.setState({ reloading: false });
  }

  // The Shell passes the pathname as `resetKey`: navigating away from the
  // crash screen (the Sidebar around the boundary still works) retries the
  // next route's chunk instead of pinning the app on the crash screen.
  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null, reloading: false });
    }
  }

  handleManualReload = () => {
    clearChunkReloadGuard();
    (this.props.reload ?? defaultReload)();
  };

  render() {
    const { error, reloading } = this.state;
    if (!error) return this.props.children;
    // Anything that is not a chunk-load failure belongs to the outer
    // (Sentry) boundary — rethrowing from render propagates it there.
    if (!isChunkLoadError(error)) throw error;
    if (reloading) return <Loading message="Updating…" />;
    return <ChunkCrashScreen onReload={this.handleManualReload} />;
  }
}
